import { memo, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button } from './Button';
import { ConfirmDialog } from './ConfirmDialog';
import { useToast } from './Toast';
import { groupConversations } from '../hooks/useConversations';
import { useFocusTrap } from '../hooks/useFocusTrap';
import {
  AlertIcon, CheckIcon, CloseIcon, CloudIcon, LockIcon, MessageIcon, PencilIcon, PlusIcon,
  RefreshIcon, SearchIcon, TrashIcon,
} from './icons';
import './ConversationSidebar.css';

export const SIDEBAR_ID = 'conversation-sidebar';
const UNTITLED = 'Nouvelle conversation';

const dateFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
const formatDate = (iso) => {
  const at = Date.parse(iso || '');
  return Number.isNaN(at) ? '' : dateFormat.format(at);
};

// ── Une conversation de la liste : ouvrir, renommer en place, supprimer ──
const ConversationItem = memo(function ConversationItem({ item, active, opening, onSelect, onRename, onDelete }) {
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);
  const renameButtonRef = useRef(null);
  const title = item.title || UNTITLED;

  useEffect(() => {
    if (renaming) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [renaming]);

  const startRename = () => {
    setDraft(item.title || '');
    setRenaming(true);
  };

  const finishRename = async (save) => {
    if (!renaming) return;
    setRenaming(false);
    requestAnimationFrame(() => renameButtonRef.current?.focus());
    const next = draft.replace(/\s+/g, ' ').trim();
    if (save && next && next !== item.title) await onRename(item.id, next);
  };

  return (
    <li className={`conv-item ${active ? 'is-active' : ''} ${renaming ? 'is-renaming' : ''}`}>
      {renaming ? (
        <form
          className="conv-item__rename"
          onSubmit={(event) => {
            event.preventDefault();
            finishRename(true);
          }}
        >
          <label className="sr-only" htmlFor={`rename-${item.id}`}>Nouveau titre de la conversation</label>
          <input
            id={`rename-${item.id}`}
            ref={inputRef}
            value={draft}
            maxLength={200}
            placeholder={UNTITLED}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => finishRename(true)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                finishRename(false);
              }
            }}
          />
        </form>
      ) : (
        <button
          type="button"
          className="conv-item__main"
          aria-current={active ? 'page' : undefined}
          aria-busy={opening || undefined}
          title={`${title}${item.updated_at ? ` — ${formatDate(item.updated_at)}` : ''}`}
          onClick={() => onSelect(item.id)}
        >
          <MessageIcon size={14} className="conv-item__icon" />
          <span className="conv-item__title">{title}</span>
          {opening && <span className="conv-item__spinner" aria-hidden="true" />}
        </button>
      )}

      {!renaming && (
        <span className="conv-item__actions">
          <Button
            ref={renameButtonRef}
            variant="ghost"
            size="sm"
            icon={PencilIcon}
            iconSize={13}
            iconOnly
            className="conv-item__action"
            aria-label={`Renommer « ${title} »`}
            title="Renommer"
            onClick={startRename}
          />
          <Button
            variant="ghost"
            size="sm"
            tone="danger"
            icon={TrashIcon}
            iconSize={13}
            iconOnly
            className="conv-item__action"
            aria-label={`Supprimer « ${title} »`}
            title="Supprimer"
            onClick={() => onDelete(item)}
          />
        </span>
      )}
    </li>
  );
});

// ── Pied : état de la sauvegarde de la conversation active ──
function SyncStatus({ sync }) {
  if (!sync || sync.status === 'idle') return null;
  const busy = sync.status === 'pending' || sync.status === 'saving';
  const failed = sync.status === 'error' || sync.status === 'retrying';
  return (
    <div className={`sidebar-sync ${failed ? 'is-error' : ''}`} role={failed ? 'alert' : undefined}>
      {busy && <><CloudIcon size={13} /><span>Enregistrement…</span></>}
      {sync.status === 'saved' && <><CheckIcon size={13} /><span>Conversation enregistrée</span></>}
      {failed && (
        <>
          <AlertIcon size={13} />
          <span className="sidebar-sync__text">{sync.error}</span>
          {sync.status === 'error' && (
            <button type="button" className="sidebar-sync__retry" onClick={sync.retry}>Réessayer</button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Barre latérale « Historique » (UI-02).
 * - Bureau : colonne repliable (état mémorisé par App).
 * - Mobile (< 768 px) : tiroir modal avec voile, piège de focus, Échap ferme.
 * - Sans contexte entreprise : historique désactivé, avec explication.
 */
export const ConversationSidebar = memo(function ConversationSidebar({ history, open, isMobile, onClose, onSelect, onNew, onDelete, openingId, sync }) {
  const toast = useToast();
  const rootRef = useRef(null);
  const searchId = useId();
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const modal = isMobile && open;

  useFocusTrap(rootRef, modal);

  // Repliée : hors de l'ordre de tabulation et des lecteurs d'écran (attribut inert)
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [open]);

  // Tiroir mobile : focus dans le tiroir à l'ouverture, Échap ferme
  useEffect(() => {
    if (!modal) return undefined;
    const frame = requestAnimationFrame(() => {
      rootRef.current?.querySelector('.sidebar-new')?.focus();
    });
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [modal, onClose]);

  const groups = useMemo(() => groupConversations(history.items), [history.items]);

  // Référence stable : les éléments mémoïsés de la liste ne se redessinent pas à chaque rendu
  const historyRename = history.rename;
  const rename = useCallback(async (id, title) => {
    try {
      await historyRename(id, title);
    } catch (err) {
      toast(err.message === 'Le titre ne peut pas être vide' ? err.message : 'Renommage impossible', { tone: 'error' });
    }
  }, [historyRename, toast]);

  const confirmDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      await onDelete(toDelete.id);
      toast('Conversation supprimée');
      setToDelete(null);
      // Le bouton d'origine a disparu avec la conversation : focus sur la recherche
      requestAnimationFrame(() => document.getElementById(searchId)?.focus());
    } catch {
      toast('Suppression impossible', { tone: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const searching = !!history.query.trim();
  const deletingRef = useRef(deleting);
  deletingRef.current = deleting;
  const cancelDelete = useCallback(() => {
    if (!deletingRef.current) setToDelete(null);
  }, []);

  return (
    <>
      {isMobile && (
        <div
          className={`sidebar-scrim ${open ? 'is-visible' : ''}`}
          aria-hidden="true"
          onClick={onClose}
        />
      )}

      <aside
        id={SIDEBAR_ID}
        ref={rootRef}
        className={`sidebar ${open ? 'is-open' : 'is-closed'} ${isMobile ? 'is-mobile' : 'is-desktop'}`}
        aria-label="Historique des conversations"
        {...(modal ? { role: 'dialog', 'aria-modal': 'true' } : {})}
      >
        <div className="sidebar-inner">
          <div className="sidebar-top">
            <Button variant="primary" icon={PlusIcon} className="sidebar-new" onClick={onNew}>
              <span>Nouvelle conversation</span>
            </Button>
            {isMobile && (
              <Button icon={CloseIcon} iconOnly className="sidebar-close" aria-label="Fermer l'historique" onClick={onClose} />
            )}
          </div>

          {history.enabled ? (
            <>
              <div className="sidebar-search" role="search">
                <label htmlFor={searchId} className="sr-only">Rechercher dans l'historique</label>
                <SearchIcon size={14} className="sidebar-search__icon" />
                <input
                  id={searchId}
                  type="search"
                  value={history.query}
                  placeholder="Rechercher…"
                  autoComplete="off"
                  onChange={(event) => history.setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape' && history.query) {
                      event.preventDefault();
                      event.stopPropagation();
                      history.setQuery('');
                    }
                  }}
                />
                {(history.searching || (searching && history.status === 'loading')) && (
                  <span className="sidebar-search__spinner" aria-hidden="true" />
                )}
                {history.query && !history.searching && history.status !== 'loading' && (
                  <button
                    type="button"
                    className="sidebar-search__clear"
                    aria-label="Effacer la recherche"
                    onClick={() => history.setQuery('')}
                  >
                    <CloseIcon size={12} />
                  </button>
                )}
              </div>

              <nav className="sidebar-list" aria-label="Conversations" aria-busy={history.status === 'loading' || undefined}>
                {history.status === 'loading' && !history.items.length && (
                  <div className="sidebar-skeleton" role="status">
                    {[72, 88, 64, 80, 56].map((w, i) => (
                      <span key={i} className="sidebar-skeleton__row" style={{ width: `${w}%` }} aria-hidden="true" />
                    ))}
                    <span className="sr-only">Chargement de l'historique…</span>
                  </div>
                )}

                {history.status === 'error' && (
                  <div className="sidebar-state is-error" role="alert">
                    <AlertIcon size={16} />
                    <p>Impossible de charger l'historique.</p>
                    <Button size="sm" icon={RefreshIcon} className="sidebar-state__button" onClick={history.refresh}>
                      Réessayer
                    </Button>
                  </div>
                )}

                {history.status === 'ready' && !history.items.length && (
                  <div className="sidebar-state">
                    <MessageIcon size={18} />
                    {searching ? (
                      <p>Aucun résultat pour « {history.query.trim()} ».</p>
                    ) : (
                      <p>Aucune conversation pour l'instant. Vos échanges apparaîtront ici.</p>
                    )}
                  </div>
                )}

                {/* Annonce du nombre de résultats d'une recherche */}
                <span className="sr-only" aria-live="polite">
                  {searching && history.status === 'ready'
                    ? `${history.items.length} conversation${history.items.length > 1 ? 's' : ''} trouvée${history.items.length > 1 ? 's' : ''}`
                    : ''}
                </span>

                {history.items.length > 0 && groups.map((group) => (
                  <section key={group.id} className="sidebar-group" aria-labelledby={`${searchId}-${group.id}`}>
                    <h3 id={`${searchId}-${group.id}`} className="sidebar-group__title">{group.label}</h3>
                    <ul className="sidebar-group__list">
                      {group.items.map((item) => (
                        <ConversationItem
                          key={item.id}
                          item={item}
                          active={item.id === history.activeId}
                          opening={item.id === openingId}
                          onSelect={onSelect}
                          onRename={rename}
                          onDelete={setToDelete}
                        />
                      ))}
                    </ul>
                  </section>
                ))}

                {history.hasMore && history.status === 'ready' && (
                  <Button
                    size="sm"
                    className="sidebar-more"
                    onClick={() => history.loadMore().catch(() => toast('Chargement impossible', { tone: 'error' }))}
                    loading={history.loadingMore}
                  >
                    Afficher plus
                  </Button>
                )}
              </nav>

              <footer className="sidebar-footer">
                <SyncStatus sync={sync} />
              </footer>
            </>
          ) : (
            <div className="sidebar-disabled" role="note">
              <LockIcon size={18} />
              <p className="sidebar-disabled__title">Historique indisponible</p>
              <p>
                Ouvrez l'assistant depuis la plateforme digiTRANS pour retrouver vos conversations.
                Sans identification de votre entreprise, les échanges ne sont pas enregistrés.
              </p>
            </div>
          )}
        </div>
      </aside>

      <ConfirmDialog
        open={!!toDelete}
        title="Supprimer la conversation ?"
        description={toDelete ? `« ${toDelete.title || UNTITLED} » sera supprimée définitivement.` : ''}
        confirmLabel="Supprimer"
        busy={deleting}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
});
